/**
 * Shared HTTP transport failure classification (fetch/XHR adapters, retries).
 * Offline sync UX copy stays in the sync module; this file is dependency-free besides DOM types.
 */

/**
 * Single allowlist used for both Response-based retries and `HTTP <n>` fragments in thrown Error.message.
 * Excludes bare 500/501/etc. where bodies often carry stable business errors worth surfacing immediately.
 */
const RETRYABLE_HTTP_STATUSES = new Set<number>([408, 429, 502, 503, 504]);

/** Parses a leading `HTTP <status>` from messages (e.g. `HTTP 503: …`). */
export function parseHttpStatusFromErrorMessage(message: string): number | null {
  const m = /^HTTP (\d+)/.exec(message);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

/**
 * When `fetch` returned a Response, statuses that merit a retry before handling the body
 * (gateway / overload / deadline / rate limit).
 */
export function isRetryableHttpResponseStatus(status: number): boolean {
  return RETRYABLE_HTTP_STATUSES.has(status);
}

/**
 * Thrown/low-level failures (network, timeout DOMException, XHR-compat Error shapes).
 * Uses {@link RETRYABLE_HTTP_STATUSES} for `HTTP n` fragments in `.message`.
 */
export function isRetryableHttpTransportThrownFailure(e: unknown): boolean {
  if (e instanceof TypeError) return true;
  if (typeof DOMException !== 'undefined' && e instanceof DOMException) {
    return e.name === 'TimeoutError' || e.name === 'AbortError' || e.name === 'NetworkError';
  }
  if (!(e instanceof Error)) return false;
  if (/network error/i.test(e.message)) return true;
  if (e.name === 'TimeoutError' || e.name === 'AbortError') return true;
  if (e.message === 'TimeoutError' || e.message === 'AbortError') return true;
  const status = parseHttpStatusFromErrorMessage(e.message);
  if (status == null) return false;
  return RETRYABLE_HTTP_STATUSES.has(status);
}
